import { computed, inject, Injectable, Signal } from '@angular/core';
import {
  loadAttributes,
  loadDocumentTypes,
  loadDocumentTypesTableData,
  loadSubmissionDocument,
  loadSubmissionDocumentTypes,
  loadSubmissions,
  loadSubmissionsTableData,
} from '../selectors';
import { Store } from '@ngrx/store';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';
import {
  CONFIGURATION_DOCUMENT_TYPE_ACTIONS,
  CONFIGURATION_SUBMISSION_TYPE_ACTIONS,
} from '../actions';
import {
  ConfigSubmissions,
  ConfigSubmissionsData,
  DocumentTypes,
  DocumentTypesData,
  SubmissionAttributeType,
  SubmissionDocument,
  SubmissionDocumentType,
} from '../types';
import { WcgTableData } from '@wcg/frontend-framework';
import { mapDAttributeToSAttribute } from '../utils';
import { WcgTableValues } from '@shared/interfaces/wcg-table-utils.interface';

@Injectable()
export class ConfigurationsFacadeService {
  private readonly _store = inject(Store);

  /**
   * @DATA FETCHERS
   * */
  documentTypes = toSignal(
    this._store.select(loadDocumentTypes).pipe(filter(Boolean)),
  ) as Signal<DocumentTypes>;

  loadDocumentTypesTableData = toSignal(
    this._store.select(loadDocumentTypesTableData).pipe(filter(Boolean)),
  ) as Signal<WcgTableData>;

  submissions = toSignal(
    this._store.select(loadSubmissions).pipe(filter(Boolean)),
  ) as Signal<ConfigSubmissions>;

  submissionAttributes = toSignal(
    this._store.select(loadAttributes).pipe(
      filter(Boolean),
      map((attributes) => mapDAttributeToSAttribute(attributes.data)),
    ),
  ) as Signal<SubmissionAttributeType[]>;

  submissionDocument = toSignal(
    this._store.select(loadSubmissionDocument),
  ) as Signal<SubmissionDocument>;

  loadSubmissionsTableData = toSignal(
    this._store.select(loadSubmissionsTableData).pipe(filter(Boolean)),
  ) as Signal<WcgTableData>;

  loadSubmissionDocumentTypes = toSignal(
    this._store.select(loadSubmissionDocumentTypes).pipe(filter(Boolean)),
  ) as Signal<DocumentTypes>;

  /**
   * @DATA FETCHERS END
   * */

  /**
   * @Data Massaging
   * */
  submissionDocumentTypes: Signal<SubmissionDocumentType[]> = computed(() =>
    this.loadSubmissionDocumentTypes()?.data.map((d) => ({
      id: d.id,
      name: d.name,
    })),
  );
  /**
   * @Data Massaging Ends
   * */

  /**
   * @ACTION DISPATCHERS
   * */

  loadDocumentTypes(params: WcgTableValues) {
    this._store.dispatch(
      CONFIGURATION_DOCUMENT_TYPE_ACTIONS.loadDocumentTypes({
        params,
      }),
    );
  }

  loadAttributes(params: WcgTableValues) {
    this._store.dispatch(
      CONFIGURATION_DOCUMENT_TYPE_ACTIONS.loadAttributes({
        params,
      }),
    );
  }

  loadSubmissions(params: WcgTableValues) {
    this._store.dispatch(
      CONFIGURATION_SUBMISSION_TYPE_ACTIONS.loadSubmissionList({
        params,
      }),
    );
  }

  fetchSubmissionDocumentType(submission: ConfigSubmissionsData): void {
    this._store.dispatch(
      CONFIGURATION_SUBMISSION_TYPE_ACTIONS.loadDocumentTypesForSubmission({
        submission,
      }),
    );
  }

  deleteDocumentType(data: DocumentTypesData): void {
    this._store.dispatch(
      CONFIGURATION_DOCUMENT_TYPE_ACTIONS.checkCanDeleteDocument({
        data,
      }),
    );
  }
  /**
   * @ACTION DISPATCHERS END
   * */
}
